/**
 * Locates the bubble's own <script> tag and reads its data attributes.
 *
 * The host page may have many script tags. We identify ours by:
 *   1. The current `document.currentScript` if available (most reliable).
 *   2. A fallback scan for any script with `data-tenant-key`.
 *
 * The marketing config API URL and key are baked into the bundle at
 * build time, so the only attribute tenants need to provide is
 * `data-tenant-key`.
 */

import type { LoaderConfig } from '../types/public-config';

declare global {
  interface ImportMetaEnv {
    readonly BUBBLE_CONFIG_API_URL?: string;
    readonly BUBBLE_CONFIG_API_KEY?: string;
  }
}

const BUILT_IN_CONFIG_API_URL: string =
  import.meta.env.BUBBLE_CONFIG_API_URL ?? '';
const BUILT_IN_CONFIG_API_KEY: string =
  import.meta.env.BUBBLE_CONFIG_API_KEY ?? '';

export function findScriptTag(): HTMLScriptElement | null {
  // currentScript is the most reliable signal during initial parse.
  // It's null when the script is loaded async after parsing — fall back to scan.
  const current = document.currentScript;
  if (current && current instanceof HTMLScriptElement && current.dataset.tenantKey) {
    return current;
  }
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[data-tenant-key]');
  return scripts.length > 0 ? scripts[scripts.length - 1] : null;
}

export function readLoaderConfig(tag: HTMLScriptElement): LoaderConfig {
  const tenantKey = (tag.dataset.tenantKey ?? '').trim();
  // Allow data-* overrides for QA / staging, otherwise use the
  // build-time baked values.
  const configApiUrl =
    (tag.dataset.configApiUrl ?? '').trim() || BUILT_IN_CONFIG_API_URL;
  const configApiKey =
    (tag.dataset.configApiKey ?? '').trim() || BUILT_IN_CONFIG_API_KEY;

  if (!tenantKey) throw new Error('data-tenant-key is required');
  if (!configApiUrl) {
    throw new Error('config API URL is missing — bundle was built without BUBBLE_CONFIG_API_URL');
  }
  if (!configApiKey) {
    throw new Error('config API key is missing — bundle was built without BUBBLE_CONFIG_API_KEY');
  }

  return { tenantKey, configApiUrl, configApiKey };
}
